/**
 * Centralized Chart.js theming for Kingpin's dark, indigo-accented dashboard.
 * Builders return option objects ready to spread into <Line>/<Bar>/<Doughnut> components.
 */

export const chartColors = {
  brand: '#818cf8',           // indigo-400 — primary accent on dark
  brandStrong: '#6366f1',     // indigo-500
  brandFill: 'rgba(99, 102, 241, 0.18)',
  brandFillStrong: 'rgba(99, 102, 241, 0.35)',
  grid: 'rgba(148, 163, 184, 0.10)',
  gridStrong: 'rgba(148, 163, 184, 0.18)',
  text: '#94a3b8',
  textStrong: '#cbd5e1',
  surface: '#0f172a',
  surfaceRaised: '#1e293b',
  severityCritical: '#f87171',
  severityHigh: '#fb923c',
  severityMedium: '#facc15',
  severityLow: '#38bdf8',
  severityInfo: '#94a3b8',
};

const baseFont = {
  family: "system-ui, -apple-system, 'Segoe UI', 'Roboto', sans-serif",
  size: 11,
  weight: '500',
};

const tooltipBase = {
  backgroundColor: 'rgba(15, 23, 42, 0.96)',
  borderColor: 'rgba(99, 102, 241, 0.45)',
  borderWidth: 1,
  titleColor: '#e2e8f0',
  titleFont: { ...baseFont, weight: '600', size: 12 },
  bodyColor: '#cbd5e1',
  bodyFont: baseFont,
  cornerRadius: 8,
  padding: 12,
  displayColors: false,
  caretSize: 6,
};

/** Color a percentage by severity threshold. Used to color bars in framework comparisons. */
export function severityForCompliance(value) {
  if (value >= 80) return chartColors.brand;
  if (value >= 60) return chartColors.severityMedium;
  if (value >= 40) return chartColors.severityHigh;
  return chartColors.severityCritical;
}

/** Line chart options for percentage-based time series (compliance trend). */
export function lineChartOptions({ tooltipLabel } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipBase,
        callbacks: tooltipLabel
          ? { label: (ctx) => tooltipLabel(ctx) }
          : { label: (ctx) => `${ctx.parsed.y.toFixed(1)}%` },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        border: { display: false },
        ticks: {
          color: chartColors.text,
          font: baseFont,
          maxRotation: 0,
          autoSkipPadding: 16,
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: chartColors.grid, drawBorder: false },
        border: { display: false },
        ticks: {
          color: chartColors.text,
          font: baseFont,
          callback: (v) => `${v}%`,
          stepSize: 25,
        },
      },
    },
  };
}

/** Horizontal bar chart options for percentage comparisons (frameworks, controls). */
export function horizontalBarChartOptions({ tooltipLabel } = {}) {
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipBase,
        callbacks: tooltipLabel
          ? { label: (ctx) => tooltipLabel(ctx) }
          : { label: (ctx) => `${ctx.parsed.x.toFixed(1)}%` },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        grid: { color: chartColors.grid, drawBorder: false },
        border: { display: false },
        ticks: {
          color: chartColors.text,
          font: baseFont,
          callback: (v) => `${v}%`,
          stepSize: 25,
        },
      },
      y: {
        grid: { display: false, drawBorder: false },
        border: { display: false },
        ticks: {
          color: chartColors.textStrong,
          font: { ...baseFont, weight: '600' },
        },
      },
    },
  };
}

/** Pre-built dataset for a line chart with the brand fill. */
export function lineDataset(label, data) {
  return {
    label,
    data,
    borderColor: chartColors.brandStrong,
    backgroundColor: chartColors.brandFill,
    pointBackgroundColor: chartColors.brand,
    pointBorderColor: chartColors.surface,
    pointBorderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    tension: 0.4,
    fill: true,
    borderWidth: 2,
  };
}

/** Bar dataset where each bar is colored by severity threshold. */
export function severityBarDataset(label, values) {
  return {
    label,
    data: values,
    backgroundColor: values.map((v) => severityForCompliance(v)),
    borderRadius: 6,
    borderSkipped: false,
    barThickness: 18,
    maxBarThickness: 24,
  };
}
